// components/profile/ProfileHeader.jsx
import React from 'react';
import { useDarkMode } from '../darkmode/DarkModeContext';

const ProfileHeader = ({ user, isEditing, onEditToggle }) => {
  const { isDarkMode } = useDarkMode();

  const getInitials = (name, email) => {
    if (name) {
      return name.split(' ').map(word => word.charAt(0)).join('').substring(0, 2).toUpperCase();
    }
    return email ? email.charAt(0).toUpperCase() : 'U';
  };

  const getJoinDate = (createdAt) => {
    if (!createdAt) return 'Tidak diketahui';
    const date = new Date(createdAt);
    return date.toLocaleDateString('id-ID', { 
      year: 'numeric', 
      month: 'long' 
    });
  };

  return (
    <div className={`${
      isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
    } rounded-xl p-6 border shadow-sm transition-colors`}>
      <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-6">
        {/* Avatar */}
        <div className="relative">
          <div className="w-24 h-24 rounded-full bg-gradient-to-r from-green-700 to-green-500 flex items-center justify-center text-white font-bold text-2xl shadow-lg">
            {getInitials(user?.name, user?.email)}
          </div>
          
          {/* Status Badge */}
          <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full border-2 ${
            isDarkMode ? 'border-gray-800' : 'border-white'
          } ${user?.isEmailVerified ? 'bg-green-500' : 'bg-yellow-500'} flex items-center justify-center`}>
            {user?.isEmailVerified ? (
              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            )}
          </div>
        </div>

        {/* User Info */}
        <div className="flex-1 text-center sm:text-left">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className={`text-2xl font-bold ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {user?.name || 'Nama Pengguna'}
              </h1>
              <p className={`text-sm ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              } mt-1`}>
                {user?.email}
              </p>
            </div>

            {/* Edit Button */}
            <button
              onClick={onEditToggle}
              className={`mt-4 sm:mt-0 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                isEditing
                  ? `border-2 ${isDarkMode ? 'border-red-500 text-red-400 hover:bg-red-500/10' : 'border-red-500 text-red-600 hover:bg-red-50'}`
                  : `border-2 ${isDarkMode ? 'border-green-700 text-green400 hover:bg-green-700/10' : 'border-green-700 text-green-800 hover:bg-green-50'}`
              }`}
            >
              {isEditing ? 'Batal Edit' : 'Edit Profile'}
            </button>
          </div>

          {/* User Stats */}
          <div className="flex flex-wrap justify-center sm:justify-start gap-6 mt-4">
            <div className="text-center">
              <div className={`text-lg font-semibold ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {user?.role === 'admin' ? 'Admin' : 'Customer'}
              </div>
              <div className={`text-xs ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Role
              </div>
            </div>

            <div className="text-center">
              <div className={`text-lg font-semibold ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {user?.isEmailVerified ? 'Terverifikasi' : 'Belum Terverifikasi'}
              </div>
              <div className={`text-xs ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Status Email
              </div>
            </div>

            <div className="text-center">
              <div className={`text-lg font-semibold ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {getJoinDate(user?.createdAt)}
              </div>
              <div className={`text-xs ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Bergabung
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Verification Alert */}
      {!user?.isEmailVerified && (
        <div className="mt-6 p-4 bg-yellow-500/20 border border-yellow-500/50 rounded-lg">
          <div className="flex items-start space-x-3">
            <svg className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="text-yellow-400 font-medium text-sm">
                Email Belum Terverifikasi
              </p>
              <p className="text-yellow-300 text-xs mt-1">
                Silakan cek email Anda dan klik link verifikasi untuk mengaktifkan akun sepenuhnya.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileHeader;